/**
 * AvailabilityEditor — declarative capacity control for operators.
 *
 * Phase C of the Availability Orchestration Layer. Renders the current
 * resolved state + a mode selector that writes through the canonical
 * `setContractorAvailability` helper. `at_capacity` is read-only
 * (derived) — operators can adjust `weeklyJobCap` via the Limited mode.
 *
 * Hard rules:
 *   - Never writes contractor.availabilityStatus (legacy enum); only
 *     the new declarative `availability` field.
 *   - Reason ≥10 chars required when entering Paused.
 *   - PTO requires from/until dates.
 *   - No installer-portal economics leakage — this surface is operator-only.
 */
import { useMemo, useState } from "react";
import { CalendarRange, Pause, Gauge, Zap, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import type { Contractor } from "@/data/types";
import {
  getContractorAvailability,
  setContractorAvailability,
  describeAvailability,
  type AvailabilityMode,
  type AvailabilityState,
} from "@/data/contractor/availability";

const MODE_BUTTONS: Array<{
  mode: Exclude<AvailabilityMode, "at_capacity">;
  label: string;
  icon: typeof Zap;
}> = [
  { mode: "available", label: "Available", icon: Zap },
  { mode: "limited", label: "Limited", icon: Gauge },
  { mode: "pto", label: "On PTO", icon: CalendarRange },
  { mode: "paused", label: "Paused", icon: Pause },
];

export interface AvailabilityEditorProps {
  contractor: Contractor;
  /** Set false to render the read-only portal variant. */
  editable?: boolean;
  /** Restrict modes (portal version drops admin-only options). */
  allowedModes?: Array<Exclude<AvailabilityMode, "at_capacity">>;
  /** Called after local state is updated — use to persist to DB. */
  onAfterSave?: (state: AvailabilityState) => void | Promise<void>;
  className?: string;
}

export function AvailabilityEditor({
  contractor,
  editable = true,
  allowedModes,
  onAfterSave,
  className,
}: AvailabilityEditorProps) {
  const { jobs, setContractors } = useAppData();
  const { role } = useRole();
  const resolved = useMemo(
    () => getContractorAvailability(contractor, jobs),
    [contractor, jobs],
  );

  const [draftMode, setDraftMode] = useState<
    Exclude<AvailabilityMode, "at_capacity">
  >(
    resolved.state.mode === "at_capacity" ? "limited" : resolved.state.mode,
  );
  const [weeklyCap, setWeeklyCap] = useState<number>(
    resolved.state.mode === "limited"
      ? resolved.state.weeklyJobCap
      : contractor.maxWeeklyJobs || 3,
  );
  const [reason, setReason] = useState<string>(
    resolved.state.mode === "paused" || resolved.state.mode === "limited"
      ? resolved.state.reason ?? ""
      : "",
  );
  const [from, setFrom] = useState<string>(
    resolved.state.mode === "pto" ? resolved.state.from : "",
  );
  const [until, setUntil] = useState<string>(
    resolved.state.mode === "pto"
      ? resolved.state.until
      : resolved.state.mode === "paused" && resolved.state.until
        ? resolved.state.until
        : "",
  );

  const modes = (allowedModes ?? MODE_BUTTONS.map((m) => m.mode));

  const canSave = (() => {
    if (!editable) return false;
    if (draftMode === "available") return true;
    if (draftMode === "limited") return weeklyCap > 0;
    if (draftMode === "paused") return reason.trim().length >= 10;
    if (draftMode === "pto") return Boolean(from && until && from <= until);
    return false;
  })();

  const save = () => {
    if (!canSave) return;
    let next: AvailabilityState;
    if (draftMode === "available") {
      next = { mode: "available" };
    } else if (draftMode === "limited") {
      next = {
        mode: "limited",
        weeklyJobCap: weeklyCap,
        reason: reason.trim() || undefined,
      };
    } else if (draftMode === "paused") {
      next = {
        mode: "paused",
        reason: reason.trim(),
        until: until || undefined,
      };
    } else {
      next = { mode: "pto", from, until };
    }
    setContractors((prev) =>
      setContractorAvailability(prev, contractor.id, next, {
        id: contractor.id,
        role: role || "operator",
      }),
    );
    void onAfterSave?.(next);
  };

  const toneCls =
    resolved.state.mode === "available"
      ? "text-success border-success/30 bg-success/5"
      : resolved.state.mode === "limited"
        ? "text-warning border-warning/30 bg-warning/5"
        : resolved.state.mode === "at_capacity"
          ? "text-warning border-warning/30 bg-warning/5"
          : "text-muted-foreground border-muted-foreground/20 bg-muted/30";

  return (
    <Card className={`shadow-none ${className ?? ""}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-2">
            <CalendarRange className="h-4 w-4" /> Availability
          </h2>
          <Badge variant="outline" className={`text-[10px] ${toneCls}`}>
            {describeAvailability(resolved.state)}
          </Badge>
        </div>

        {resolved.isDerived && (
          <p className="text-[10px] text-warning flex items-center gap-1 mb-2">
            <AlertTriangle className="h-3 w-3" />
            Derived from active job count — declare Limited to adjust the cap.
          </p>
        )}

        {!editable ? (
          <p className="text-[11px] text-muted-foreground">
            Last updated{" "}
            {resolved.declaredAt
              ? new Date(resolved.declaredAt).toLocaleDateString()
              : "—"}
            {resolved.declaredBy ? ` by ${resolved.declaredBy}` : ""}.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {MODE_BUTTONS.filter((m) => modes.includes(m.mode)).map((m) => {
                const Icon = m.icon;
                const active = draftMode === m.mode;
                return (
                  <button
                    key={m.mode}
                    type="button"
                    onClick={() => setDraftMode(m.mode)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] border transition-colors ${
                      active
                        ? "bg-primary/15 text-primary ring-1 ring-primary/30 border-transparent"
                        : "bg-background text-muted-foreground border-border hover:bg-muted"
                    }`}
                  >
                    <Icon className="h-3 w-3" />
                    {m.label}
                  </button>
                );
              })}
            </div>

            {draftMode === "limited" && (
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Weekly job cap
                </label>
                <Input
                  type="number"
                  min={1}
                  value={weeklyCap}
                  onChange={(e) => setWeeklyCap(Number(e.target.value) || 0)}
                  className="h-8 text-xs w-24"
                />
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Optional context (e.g. crew member out)"
                  className="text-xs min-h-[50px]"
                />
              </div>
            )}

            {draftMode === "pto" && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    From
                  </label>
                  <Input
                    type="date"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Until
                  </label>
                  <Input
                    type="date"
                    value={until}
                    onChange={(e) => setUntil(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            )}

            {draftMode === "paused" && (
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Reason (required, ≥10 chars)
                </label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why is this installer paused?"
                  className="text-xs min-h-[60px]"
                />
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Until (optional)
                </label>
                <Input
                  type="date"
                  value={until}
                  onChange={(e) => setUntil(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>
            )}

            <div className="flex justify-end mt-3">
              <Button size="sm" onClick={save} disabled={!canSave}>
                Save availability
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
